<template>
  <AppDialog
    :show="show"
    :title="title"
    append-to-body
    class="settings-smart-notifications-dialog"
    @close="emitClose"
  >
    <ValidationObserver
      id="settings-smart-notifications-form"
      ref="observer"
      tag="form"
    >
      <div class="form-group d-flex gap-15">
        <div class="w-full">
          <ValidationProvider
            v-slot="{ errors }"
            rules="required"
            vid="name"
            slim
          >
            <div :class="{ 'has-error': errors[0] }">
              <label
                for="form-name"
                class="control-label"
              >
                Name
              </label>
              <AppInput
                id="form-name"
                v-model="form.name"
                placeholder="Name your smart notification"
              />
              <span
                v-if="errors[0]"
                class="help-block"
              >
                {{ errors[0] }}
              </span>
            </div>
          </ValidationProvider>
        </div>

        <ValidationProvider
          v-slot="{ errors }"
          rules="required"
          vid="frequency"
          name="frequency"
          slim
        >
          <div
            :class="{ 'has-error': errors[0] }"
            class="frequency-wrapper"
          >
            <label
              for="form-frequency"
              class="control-label"
            >
              Frequency
            </label>
            <AppSelect
              id="form-frequency"
              v-model="form.frequency"
              :options="frequencyOptions"
              :select2-options="{ ...select2Options, width: '14rem' }"
            />
            <span
              v-if="errors[0]"
              class="help-block"
            >
              {{ errors[0] }}
            </span>
          </div>
        </ValidationProvider>
      </div>

      <div
        v-if="weeklyMonthlyHint"
        class="hint my-20"
      >
        {{ weeklyMonthlyHint }}
      </div>

      <hr />

      <div class="divider">
        <span>When</span>
      </div>

      <div class="hint">Specify the metrics and values to monitor from members or teams within your workforce</div>

      <div class="d-flex flex-lg-nowrap mb-30 metric-input-group gap-15 flex-wrap">
        <div class="d-flex gap-15 flex-lg-nowrap flex-wrap">
          <ValidationProvider
            v-slot="{ errors }"
            rules="required"
            vid="metric_type"
            slim
          >
            <div
              class="metric-wrapper"
              :class="{ 'has-error': errors[0] }"
            >
              <label
                for="form-metric"
                class="control-label align-items-baseline"
              >
                Metric
                <i
                  v-if="form.frequency === 'daily'"
                  v-tooltip="tooltipOptions"
                  class="hi hi-info-filled ml-5"
                  :title="dailyMetricTooltipCopy"
                ></i>
              </label>
              <AppSelect
                id="form-metric"
                v-model="form.metric_type"
                :options="metricOptions"
                :select2-options="select2Options"
                @input="onMetricChange"
              />
              <span
                v-if="errors[0]"
                class="help-block"
              >
                {{ errors[0] }}
              </span>
            </div>
          </ValidationProvider>

          <div
            v-if="showMetricFilter"
            :class="{ 'has-time-unit': !metricWithoutTimeUnit }"
            class="d-flex set-value-of gap-10"
          >
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              vid="condition"
              slim
            >
              <div :class="{ 'has-error': errors[0] }">
                <label
                  for="form-condition"
                  class="control-label"
                >
                  Has a set value of
                </label>
                <AppSelect
                  id="form-condition"
                  v-model="form.condition"
                  class="condition"
                  :options="conditionOptions"
                  :select2-options="select2Options"
                />
                <span
                  v-if="errors[0]"
                  class="help-block"
                >
                  {{ errors[0] }}
                </span>
              </div>
            </ValidationProvider>
          </div>
        </div>
        <div
          v-if="showMetricFilter"
          class="d-flex gap-15"
        >
          <ValidationProvider
            v-slot="{ errors }"
            name="value"
            :rules="computedConditionValue.rules"
            vid="condition_value"
            slim
          >
            <div
              v-if="metricWithoutTimeUnit"
              :class="{ 'has-error': errors[0] }"
              class="condition-value-wrapper"
            >
              <label class="control-label"> &nbsp; </label>
              <div class="input-group">
                <AppInput
                  v-model="form.condition_value"
                  :placeholder="computedConditionValue.placeholder"
                  data-test-id="condition-value-input"
                />
                <span class="input-group-addon">{{ computedConditionValue.addon }}</span>
              </div>
              <span
                v-if="errors[0]"
                class="help-block"
              >
                {{ errors[0] }}
              </span>
            </div>
            <div
              v-else
              :class="{ 'has-error': errors[0] }"
              class="condition-group flex-1"
            >
              <label class="control-label"> &nbsp; </label>
              <div class="d-flex gap-8">
                <AppInput
                  v-model="form.condition_value"
                  class="condition-value-input"
                  data-test-id="condition-value-input"
                  :placeholder="computedConditionValue.placeholder"
                />
                <AppSelect
                  id="form-time_unit"
                  v-model="form.time_unit"
                  class="time_unit"
                  :options="timeUnitOptions"
                  :select2-options="{ ...select2Options, width: '115px' }"
                  always-emit
                  @input="recalculateMetrics"
                />
              </div>
              <span
                v-if="errors[0]"
                class="help-block"
              >
                {{ errors[0] }}
              </span>
            </div>
          </ValidationProvider>
        </div>
      </div>

      <div
        v-if="isGeoIpChangesMetric || isGeoIpListMatchMetric"
        class="form-group mb-30"
      >
        <label
          for="form-geo-ip-scope"
          class="control-label"
        >
          {{ geoIpScopeLabel }}
        </label>
        <AppSelect
          id="form-geo-ip-scope"
          v-model="form.scope"
          :options="geoIpScopeOptions"
          :select2-options="{ ...select2Options, width: '200px' }"
        />
      </div>

      <div
        v-if="isGeoIpListMatchMetric"
        class="form-group mb-30"
      >
        <label
          for="form-geo-ip-match-mode"
          class="control-label"
        >
          Trigger when the {{ form.scope }} is
        </label>
        <AppSelect
          id="form-geo-ip-match-mode"
          v-model="form.match_mode"
          :options="geoIpMatchModeOptions"
          :select2-options="{ ...select2Options, width: '200px' }"
        />
      </div>

      <ValidationProvider
        v-if="isGeoIpListMatchMetric"
        v-slot="{ errors }"
        rules="required"
        vid="list"
        slim
      >
        <div
          class="form-group mb-30"
          :class="{ 'has-error': errors[0] }"
        >
          <label
            for="form-geo-ip-list"
            class="control-label"
          >
            {{ geoIpListLabel }}
          </label>
          <AppSelect
            id="form-geo-ip-list"
            :key="`geo-ip-list-${geoIpListKey}`"
            v-model="form.list"
            select2-id="geo-ip-list-select"
            :select2-options="geoIpListSelect2Options"
          />
          <span
            v-if="errors[0]"
            class="help-block"
          >
            {{ errors[0] }}
          </span>
        </div>
      </ValidationProvider>

      <AppAlert
        v-if="metricNotification"
        type="info"
        icon-classes="hi hi-info-filled"
        :message="metricNotification"
      />

      <template v-for="CTA in CTAs">
        <div
          v-if="CTA.show"
          :key="CTA.key"
          class="dialog-warning qa-cta orange"
        >
          <i class="hi hi-info-filled"></i>
          <span>{{ CTA.text }}</span>
          <a
            :href="generateURL(CTA.url)"
            class="link"
          >
            Enable setting
          </a>
        </div>
      </template>
      <ValidationProvider
        v-if="showToolsInput"
        v-slot="{ errors }"
        rules="required"
        vid="tools"
        slim
      >
        <div :class="{ 'has-error': errors[0] }">
          <label
            for="form-tools"
            class="control-label"
          >
            {{ computedToolLabel }}
          </label>
          <AppSelect
            id="form-tools"
            :key="`tools-${toolsKey}`"
            ref="tools-select"
            v-model="tools"
            select2-id="tools-select"
            :select2-options="toolsSelect2Options"
            async
          />
          <span
            v-if="errors[0]"
            class="help-block"
          >
            {{ errors[0] }}
          </span>
        </div>
      </ValidationProvider>
      <ValidationProvider
        v-slot="{ errors }"
        rules="custom_target_rule"
        vid="target"
        slim
      >
        <div :class="{ 'has-error': errors[0] }">
          <label
            for="form-target"
            class="control-label align-items-baseline mt-10"
          >
            <span>Monitored audience</span>
            <i
              v-tooltip="deliverByTooltipOptions"
              class="hi hi-info-filled ml-5"
              :title="futureMembersTooltipCopy"
            ></i>
          </label>
          <div class="hint d-block mb-15">{{ targetDescription }}</div>
          <AppInput
            v-show="false"
            v-model="targetValue"
          />

          <AppOnOffToggle
            v-if="!is_team_lead || viewMode"
            ref="targetToggle"
            :disabled="viewMode"
            :value="targetAllMembers"
            class="text-lighter"
            :class="{ 'mb-15': !targetAllMembers }"
            @input="(value) => onTargetChange(value, 'toggle')"
          >
            All current and future members
          </AppOnOffToggle>

          <template v-if="!targetAllMembers">
            <AppTabs
              ref="targetTab"
              compact
              :selected-tab="targetTab"
            >
              <AppTab
                :label="`Members ${targetSelect.members.length ? `(${targetSelect.members.length})` : ''}`"
                tab-id="target-members"
                @tab-selected="selectTab('target', 'target-members')"
              />
              <AppTab
                :label="`Teams ${targetSelect.teams.length ? `(${targetSelect.teams.length})` : ''}`"
                tab-id="target-teams"
                @tab-selected="selectTab('target', 'target-teams')"
              />
              <AppTab
                v-if="(!is_team_lead || viewMode) && !authorIsTeamLead"
                :label="`Job Type ${targetSelect.occupations.length ? `(${targetSelect.occupations.length})` : ''}`"
                tab-id="target-occupation"
                @tab-selected="selectTab('target', 'target-occupation')"
              />
            </AppTabs>

            <div v-show="targetTab === 'target-members'">
              <AppLazySelect
                :key="`target-member-${targetKey}`"
                ref="form-target-members"
                v-model="targetSelect.members"
                eager
                append-to-body
                multiple
                async
                title="Select members"
                resource="member"
                :disabled="targetAllMembers"
                :fetcher="(payload) => fetcher(payload, 'user', ['privacy'])"
                :organization-id="organization_id"
                @input="onTargetChange"
              />
            </div>

            <AppLazySelect
              v-show="targetTab === 'target-teams'"
              :key="`target-team-${targetKey}`"
              ref="form-target-teams"
              v-model="targetSelect.teams"
              eager
              append-to-body
              multiple
              title="Select teams"
              resource="team"
              :disabled="targetAllMembers"
              :fetcher="(payload) => fetcher(payload, 'team')"
              :organization-id="organization_id"
              @input="onTargetChange"
            />

            <AppLazySelect
              v-show="targetTab === 'target-occupation'"
              :key="`target-occupation-${targetKey}`"
              ref="form-target-occupation"
              v-model="targetSelect.occupations"
              eager
              append-to-body
              multiple
              title="Select job types"
              resource="occupation"
              :disabled="targetAllMembers || is_team_lead"
              :fetcher="(payload) => fetcher(payload, 'occupation')"
              :organization-id="organization_id"
              @input="onTargetChange"
            />
          </template>
          <span
            v-if="errors[0]"
            class="help-block"
          >
            {{ errors[0] }}
          </span>
        </div>
      </ValidationProvider>

      <hr />

      <div class="divider">
        <span>Then</span>
      </div>

      <div class="hint">Select who will receive the notifications and their delivery channel</div>
      <div>
        <ValidationProvider
          v-slot="{ errors }"
          vid="recipients"
          rules="custom_recipients_rule"
          slim
        >
          <div
            class="recipients"
            :class="{ 'has-error': errors[0] }"
          >
            <label
              for="form-recipients"
              class="control-label"
            >
              Send notification to
            </label>
            <AppInput
              v-show="false"
              v-model="recipientsValue"
            />
            <AppTabs
              ref="recipientsTab"
              compact
              :selected-tab="recipientsTab"
            >
              <AppTab
                :label="`Roles ${recipientsSelect.roles.length ? `(${recipientsSelect.roles.length})` : ''}`"
                tab-id="recipients-roles"
                @tab-selected="selectTab('recipients', 'recipients-roles')"
              ></AppTab>
              <AppTab
                :label="`Members ${recipientsSelect.members.length ? `(${recipientsSelect.members.length})` : ''}`"
                tab-id="recipients-members"
                @tab-selected="selectTab('recipients', 'recipients-members')"
              ></AppTab>
            </AppTabs>

            <AppLazySelect
              v-show="recipientsTab === 'recipients-members'"
              ref="form-recipients"
              v-model="recipientsSelect.members"
              eager
              append-to-body
              multiple
              title="Select members"
              resource="member"
              :fetcher="(payload) => fetcher(payload, 'user', ['roles', 'not_strict'])"
              :organization-id="organization_id"
              @input="onRecipientChange"
            ></AppLazySelect>
            <AppSelect
              v-show="recipientsTab === 'recipients-roles'"
              id="form-recipients"
              ref="form-recipients"
              v-model="recipientsSelect.roles"
              :options="rolesOptions"
              :select2-options="{ ...select2OptionsMultiple, placeholder: 'Select roles' }"
              @input="onRecipientChange"
            />
            <span
              v-if="errors[0]"
              class="help-block"
            >
              {{ errors[0] }}
            </span>
          </div>
        </ValidationProvider>
      </div>

      <div class="advanced-settings">
        <ValidationProvider
          v-slot="{ errors }"
          vid="deliver_by"
          name="deliver_by"
          rules="custom_deliver_by_rule"
          :detect-input="false"
          slim
        >
          <!-- eslint-disable vue/valid-v-model -->
          <div
            v-model="form.deliver_by"
            class="hidden"
          />
          <!-- eslint-enable vue/valid-v-model -->
          <div :class="{ 'has-error': errors[0] }">
            <label
              for="form-target"
              class="control-label mt-20"
            >
              Delivery channel
            </label>
            <AppCheckbox
              v-model="form.deliver_by.insights"
              disabled
            >
              <div class="d-flex align-items-center">
                <span>Show on Highlights page</span>
                <i
                  v-tooltip="deliverByTooltipOptions"
                  class="hi hi-info-filled ml-5"
                  :title="insightsDeliveryTooltipCopy"
                ></i>
              </div>
            </AppCheckbox>

            <AppCheckbox v-model="form.deliver_by.email">
              <div class="d-flex align-items-center">
                <span>Send via email</span>
              </div>
            </AppCheckbox>

            <AppCheckbox
              v-model="form.deliver_by.slack"
              :disabled="!helpers.slack_integration_enabled"
              @input="checkSlackStatus"
            >
              <div class="d-flex align-items-center">
                <span>Send to Slack</span>
                <i
                  v-tooltip="deliverByTooltipOptions"
                  class="hi hi-info-filled ml-5"
                  :title="computedDeliverByTooltipCopy"
                ></i>
              </div>
            </AppCheckbox>
            <span
              v-if="errors[0]"
              class="help-block"
            >
              {{ errors[0] }}
            </span>
          </div>
        </ValidationProvider>
      </div>

      <div
        v-if="slackStatusLoading"
        class="d-flex align-items-center justify-content-end"
      >
        <div class="spinner-wrapper mr-10">
          <span class="hi hi-spinner hi-16 text-blue" />
        </div>
        <span>Checking Slack users</span>
      </div>

      <AppAlert
        v-if="showSlackMatchWarning"
        v-tooltip="tooltipOptions"
        :title="unmatchedUsersTooltipCopy"
        type="warning"
        icon-classes="hi hi-warning"
        :message="slackMissMatchCopy"
      />

      <AppAlert
        v-if="frequencyNotification"
        type="warning"
        icon-classes="hi hi-warning"
        class="mt-20"
        :message="frequencyNotification"
      />

      <AppAlert
        v-for="alert in allAlerts"
        :key="alert"
        type="info"
        icon-classes="hi hi-info-filled"
        class="mt-20"
        :message="alert"
      />

      <div class="action-buttons">
        <AppButton @click.stop="emitClose">Cancel</AppButton>
        <AppButton
          type="primary"
          :disabled="viewMode"
          @click.stop="validate"
        >
          Save
        </AppButton>
      </div>
    </ValidationObserver>
  </AppDialog>
</template>

<script>
import Vue from 'vue';
import { regex, min_value as minValue } from 'vee-validate/dist/rules';
import { extend } from 'vee-validate';
import { mapGetters, mapState, mapActions } from 'vuex';

import { SETTINGS_SMART_NOTIFICATIONS } from '~/app_constants';
import { applyTemplate } from '~/utils/url_template';
import { secondsToHours } from '~/utils/time_utils';
import { isValidIp } from '~/utils/ip_utils';
import { SmartNotificationsSettingsResource } from '~/resources';

// Convert time String to Integer hours
const timeToHours = (time) => {
  const [hours, minutes] = time.split(':');

  return parseInt(hours) + parseInt(minutes) / 60;
};

export const minutesPerHourRule = {
  validate(value) {
    const totalHours = timeToHours(value);

    return totalHours > 0 && totalHours < 1;
  },
  message: 'Enter a value between 00:01 and 00:59',
};

export const hoursPerDayRule = {
  validate(value) {
    const totalHours = timeToHours(value);

    return totalHours > 0 && totalHours < 24;
  },
  message: 'Enter a value between 00:01 and 23:59',
};

export const hoursPerWeekRule = {
  validate(value) {
    const totalHours = timeToHours(value);

    return totalHours > 0 && totalHours < 168;
  },
  message: 'Enter a value between 00:01 and 167:59',
};

export const hoursPerMonthRule = {
  validate(value) {
    const totalHours = timeToHours(value);

    return totalHours > 0 && totalHours < 672;
  },
  message: 'Enter a value between 00:01 and 671:59',
};

export const customDeliverByRule = {
  validate(value) {
    return Object.values(value).some(Boolean);
  },
  message: 'At least one delivery channel should be selected',
};

export const customTargetRule = (authorIsTeamLead) => ({
  validate(value) {
    return Object.values(value).some(Boolean);
  },
  message: authorIsTeamLead
    ? '*Select any members or teams to continue'
    : '*Select any members, teams, or job types to continue',
  computesRequired: true,
});

export const customRecipientsRule = {
  validate(value) {
    return Object.values(value).some(Boolean);
  },
  message: '*Select any roles or members to continue',
  computesRequired: true,
};

extend('minutes_per_hour_rule', minutesPerHourRule);
extend('hours_per_day_rule', hoursPerDayRule);
extend('hours_per_week_rule', hoursPerWeekRule);
extend('hours_per_month_rule', hoursPerMonthRule);
extend('custom_deliver_by_rule', customDeliverByRule);
extend('custom_recipients_rule', customRecipientsRule);
extend('regex', regex);
extend('min_value', {
  ...minValue,
  message: 'enter a bigger number',
});

const defaultSelect2Options = {
  allowClear: false,
  width: '100%',
  placeholder: 'Select',
};
const ALL_MEMBERS = 'all';

export default {
  name: 'SmartNotificationsDialog',
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: (value) => ['create', 'edit'].includes(value),
    },
    preset: {
      type: String,
      default: '',
      validator: (value) =>
        [
          'suspicious_apps',
          'overworking',
          'underworking',
          'low_activity',
          'high_activity',
          'low_core_work',
          'unproductive_work',
          'members_using_social_media_websites',
          'members_using_AI_websites',
          'members_usings_entertainment_websites',
          'members_not_using_required_apps',
          'suspicious_application_autoclicker',
          'idle_time',
          'manual_time',
        ].includes(value),
    },
    notification: {
      type: Object,
      default: () => ({}),
    },
    activityRecordingEnabled: {
      type: Boolean,
      default: false,
    },
    monitoringUserActivityEnabled: {
      type: Boolean,
      default: false,
    },
    enableMonitoringUrl: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    // see SETTINGS_SMART_NOTIFICATIONS.defaultFormSetting
    form: {},
    firstFetch: true,
    tooltipOptions: Object.freeze({
      placement: 'top',
      html: true,
      sanitize: false,
    }),
    deliverByTooltipOptions: Object.freeze({
      placement: 'right',
    }),
    allTargetMembersHaveBeenSelected: false,
    tools: [],
    slackStatusLoading: false,
    deliverByTooltipCopy: SETTINGS_SMART_NOTIFICATIONS.copy.dialog.deliverBy.tooltipCopy,
    viewer: [],
    recipientsTab: 'recipients-roles',
    recipientsSelect: {
      members: [],
      roles: [],
    },
    targetTab: 'target-members',
    targetAllMembers: false,
    targetSelect: {
      members: [],
      teams: [],
      occupations: [],
    },
    targetKey: 1,
    dummyTimeUnit: null,
    rolesOptions: SETTINGS_SMART_NOTIFICATIONS.options.roles,
    selectedTools: [],
    toolsKey: 0,
    geoIpListKey: 0,
    metricsWithPrivacy: ['time_spent_in_app', 'time_spent_in_url'],
  }),
  computed: {
    ...mapGetters(['helpers', 'apps', 'urls', 'showUnmatchedSlackUsers', 'unmatchedSlackEmails']),
    ...mapState(['organization_id', 'alerts', 'is_team_lead']),
    allAlerts() {
      return this.alerts || [];
    },
    title() {
      return SETTINGS_SMART_NOTIFICATIONS.copy.dialog[this.type].title;
    },
    weeklyMonthlyHint() {
      return ['weekly', 'monthly'].indexOf(this.form.frequency) !== -1
        ? `${
            this.form.frequency.charAt(0).toUpperCase() + this.form.frequency.slice(1)
          } notifications will consider the average of the metric across the ${this.form.frequency.slice(0, -2)}`
        : false;
    },
    computedToolLabel() {
      return this.form.metric_type === 'time_spent_in_app' ? 'Apps' : 'Websites';
    },
    showMetricFilter() {
      // Metrics that fire on a fixed condition — no Above/Below + value input.
      // geo_ip_list_match is forced to condition='above', condition_value=0 on submit.
      const metricsWithoutFilter = ['unusual_activity', 'suspicious_activity', 'geo_ip_list_match'];

      return !metricsWithoutFilter.includes(this.form.metric_type);
    },
    showToolsInput() {
      return this.form.metric_type === 'time_spent_in_app' || this.form.metric_type === 'time_spent_in_url';
    },
    metricHasMemberPrivacy() {
      return this.metricsWithPrivacy.indexOf(this.form.metric_type) !== -1;
    },
    CTAs() {
      return [
        {
          key: 'monitoring',
          show: !this.monitoringUserActivityEnabled,
          text: SETTINGS_SMART_NOTIFICATIONS.copy.dialog.monitoringActivity,
          url: `/organizations/${this.organization_id}/addons`,
        },
        {
          key: 'record',
          show: !this.activityRecordingEnabled,
          text: SETTINGS_SMART_NOTIFICATIONS.copy.dialog.recordActivity,
          url: `/organizations/${this.organization_id}/settings/activity/monitor_user_activity`,
        },
      ];
    },
    viewMode() {
      return this.type === 'edit' && this.is_team_lead && !this.notification?.managed_by_current_user;
    },
    metricNotification() {
      const notificationsList = SETTINGS_SMART_NOTIFICATIONS.options.notifications;

      return notificationsList[this.form.metric_type] || null;
    },
    frequencyNotification() {
      const notificationsList = SETTINGS_SMART_NOTIFICATIONS.options.notifications;

      return notificationsList[this.form.frequency] || null;
    },
    metricOptions() {
      const key = ['hourly', 'daily'].indexOf(this.form.frequency) === -1 ? 'daily' : this.form.frequency;
      let options = SETTINGS_SMART_NOTIFICATIONS.options.events[key];

      // hack to force recalculation when form.time_unit is changed
      // eslint-disable-next-line no-unused-expressions
      this.dummyTimeUnit;

      if (this.form.time_unit === 'per_week') {
        options = SETTINGS_SMART_NOTIFICATIONS.options.events.weekly;
      }
      if (this.form.time_unit === 'per_month') {
        options = SETTINGS_SMART_NOTIFICATIONS.options.events.monthly;
      }

      // Count metrics aggregate by frequency window and don't have a time unit, so they
      // ride whichever events list is in scope. Monthly is not a supported window, so
      // drop them even when the daily fallback (monthly + Avg. hr/day) is shown.
      if (this.form.frequency === 'monthly') {
        const countMetrics = SETTINGS_SMART_NOTIFICATIONS.options.countMetrics;

        options = options.filter((item) => !countMetrics.includes(item.id));
      }

      // Hide metrics whose plan-feature gate is closed for this org
      options = options.filter((item) => !item.feature || this.featureEnabled(item.feature));

      // Disable time_spent metrics if all users have privacy enabled
      options = options.map((item) => {
        if (this.allMembersHavePrivacyEnabled && this.metricsWithPrivacy.includes(item.id)) {
          return { ...item, text: `${item.text} (Privacy enabled)`, disabled: true };
        }

        return item;
      });

      return options;
    },
    conditionOptions: () => SETTINGS_SMART_NOTIFICATIONS.options.condition,
    computedConditionValue() {
      let placeholder, addon, rules;

      // hack to force recalculation when form.time_unit is changed
      // eslint-disable-next-line no-unused-expressions
      this.dummyTimeUnit;

      if (this.isTimeBasedMetric()) {
        if (this.form.frequency === 'hourly') {
          placeholder = '00:30';
          addon = 'min/hr';
          rules = 'required|minutes_per_hour_rule';
        } else if (this.form.frequency === 'daily' || this.form.time_unit === 'per_day') {
          placeholder = '00:30';
          addon = this.form.frequency === 'daily' ? 'hr/day' : 'Avg. hr/day';
          rules = 'required|hours_per_day_rule';
        } else if (this.form.frequency === 'weekly' || this.form.time_unit === 'per_week') {
          placeholder = '00:30';
          addon = 'hr/week';
          rules = 'required|hours_per_week_rule';
        } else if (this.form.frequency === 'monthly' || this.form.time_unit === 'per_month') {
          placeholder = '00:30';
          addon = 'hr/month';
          rules = 'required|hours_per_month_rule';
        } else {
          console.error(`Unexpected frequency and metric combination ${this.form.frequency} ${this.form.metric_type}`);
          placeholder = '00:00';
          addon = '';
          rules = 'required';
        }
      } else if (this.isCountMetric()) {
        placeholder = '5';
        addon = this.countMetricAddon();
        // geo_ip metrics allow 0 (fire-on-any change/match); other count metrics start at 1
        rules =
          this.isGeoIpListMatchMetric || this.isGeoIpChangesMetric
            ? 'required|between:0,10000'
            : 'required|between:1,10000';
      } else {
        placeholder = '0';
        addon = '%';
        rules = 'required|between:1,100';
      }

      return { placeholder, addon, rules };
    },
    allMembersHavePrivacyEnabled() {
      return this.helpers?.privacy_enabled;
    },
    frequencyOptions() {
      return SETTINGS_SMART_NOTIFICATIONS.options.frequency;
    },
    tooltipCopy: () => SETTINGS_SMART_NOTIFICATIONS.copy.dialog.advancedSettings.tooltipCopy,
    dailyMetricTooltipCopy: () => SETTINGS_SMART_NOTIFICATIONS.copy.dialog.metric.tooltipCopy,
    select2Options() {
      return Object.freeze(defaultSelect2Options);
    },
    select2OptionsMultiple() {
      return Object.freeze({
        ...defaultSelect2Options,
        multiple: true,
      });
    },
    toolsSelect2Options() {
      return {
        ...this.select2OptionsMultiple,
        closeOnSelect: false,
        allowClear: true,
        minimumInputLength: 3,
        tokenSeparators: [','],
        // Only allow custom tools for URLs
        tags: this.form.metric_type === 'time_spent_in_url',
        createTag: (params) => {
          const term = $.trim(params.term);
          // Regex for matching string to URL
          const pattern = /^[\p{L}\p{N}.-]+$/u;
          const maximumLength = 255;

          if (term.length > maximumLength) {
            return null;
          }

          // Validate the tag with regex
          if (!pattern.test(term)) {
            return null;
          }

          // Prevent creating a new custom url if it already exists
          const existingOption = $('#tools-select')
            .find('option')
            .filter(function () {
              const option = $(this).text().toLowerCase();
              const newTag = term.toLowerCase();

              return option === newTag;
            });

          if (existingOption.length) {
            return null;
          }

          return {
            id: term,
            text: term,
            newOption: true,
          };
        },
        templateResult: (selection) => {
          const text = selection?.newOption
            ? `<span class="sn-custom-tag-text">Add</span> ${selection.text}`
            : selection.text;
          const classes = selection?.newOption ? 'new-option' : 'option';

          return $.parseHTML(`<div class="sn-custom-template-result ${classes}">${text}</div>`);
        },
        ajax: {
          delay: 250,
          dataType: 'json',
          data: (params) => {
            return {
              page: params.page || 1,
              selection: this.tools || [],
              search: params.term || '',
              selected_only: false,
              selection_type: 'normal',
            };
          },
          processResults: (data) => {
            const results =
              data?.items
                .map((item) => ({ ...item, text: item.site || item.name }))
                .filter((item) => {
                  return !(
                    // If result from BE has same text as an already selected custom url, filter it out
                    // so user cannot select the same option twice
                    (this.tools.includes(item) || this.selectedTools.map((tool) => tool.text).includes(item.text))
                  );
                }) || [];
            const pagination = data.pagination ? { ...data.pagination, more: data.pagination?.next_page || false } : {};

            return { results, pagination };
          },
          transport: (params, success) => {
            const type = this.form.metric_type === 'time_spent_in_app' ? 'app' : 'url';

            this.fetcher(params.data, type).then((response) => {
              success(response);
            });

            return { abort: () => {} };
          },
        },
      };
    },
    fromTargetSelect2Options() {
      return Object.freeze({
        ...this.select2OptionsMultiple,
        placeholder: 'Select members and teams',
      });
    },
    unmatchedUsersTooltipCopy() {
      return `<div><div style="margin-bottom: 10px">${
        this.helpers.organization_name
      } users without Slack account match: </div>
      <section>${this.unmatchedSlackEmails.join(', ')}</section></div>`;
    },
    futureMembersTooltipCopy() {
      return `Future members added to the selected groups will be automatically monitored by this notification.`;
    },
    insightsDeliveryTooltipCopy() {
      return `Results will be displayed on the Highlights page`;
    },
    computedDeliverByTooltipCopy() {
      return this.deliverByTooltipCopy.slack[this.helpers.slack_integration_enabled ? 'enabled' : 'disabled'];
    },
    slackMissMatchCopy() {
      return `Not all Slack recipients will get notifications due to mismatched email addresses between ${this.helpers.organization_name} and Slack accounts`;
    },
    showSlackMatchWarning() {
      return this.showUnmatchedSlackUsers && !this.slackStatusLoading && this.form.deliver_by.slack;
    },
    timeUnitOptions() {
      const [perHour, perDay, perWeek, perMonth] = SETTINGS_SMART_NOTIFICATIONS.options.time_unit;
      const avgPerDay = { ...perDay, text: 'Avg. hr/day' };
      const frequencyTimeUnits = {
        hourly: [perHour],
        daily: [perDay],
        weekly: [avgPerDay, perWeek],
        monthly: [avgPerDay, perMonth],
      }[this.form.frequency];

      return frequencyTimeUnits;
    },
    isAppOrURLBasedMetric() {
      const appOrURLMetric = ['time_spent_in_app', 'time_spent_in_url'];

      return appOrURLMetric.indexOf(this.form.metric_type) !== -1;
    },
    createdWithPreset() {
      return this.preset && this.preset in SETTINGS_SMART_NOTIFICATIONS.presets;
    },
    targetValue() {
      return this.targetAllMembers ? ALL_MEMBERS : Object.values(this.targetSelect).flat().join('');
    },
    targetDescription() {
      return this.authorIsTeamLead
        ? 'Select as many teams and members as you like'
        : 'Select as many teams, members, and job types as you like';
    },
    recipientsValue() {
      return Object.values(this.recipientsSelect).flat().join('');
    },
    authorIsTeamLead() {
      return this.notification?.author_role === 'team_lead' || this.is_team_lead;
    },
    metricWithoutTimeUnit() {
      return !this.isTimeBasedMetric() || (this.isTimeBasedMetric() && this.timeUnitOptions.length === 1);
    },
    isGeoIpChangesMetric() {
      return this.form.metric_type === 'geo_ip_changes';
    },
    isGeoIpListMatchMetric() {
      return this.form.metric_type === 'geo_ip_list_match';
    },
    geoIpScopeOptions() {
      if (this.isGeoIpListMatchMetric) {
        return [
          { id: 'ip', text: 'IP' },
          { id: 'city', text: 'City' },
          { id: 'country', text: 'Country' },
        ];
      }

      return [
        { id: 'ip', text: 'Any IP change' },
        { id: 'city', text: 'City changes only' },
        { id: 'country', text: 'Country changes only' },
      ];
    },
    geoIpScopeLabel() {
      return this.isGeoIpListMatchMetric ? 'Match against' : 'Counting changes in';
    },
    geoIpMatchModeOptions() {
      return [
        { id: 'in', text: 'In the list' },
        { id: 'not_in', text: 'Not in the list' },
      ];
    },
    geoIpListLabel() {
      switch (this.form.scope) {
        case 'country':
          return 'Country codes (2-letter ISO, e.g. US, BR)';
        case 'city':
          return 'Cities (one per entry)';
        default:
          return 'IPs (one per entry, IPv4 or IPv6)';
      }
    },
    geoIpListSelect2Options() {
      const scope = this.form.scope;
      const country = /^[A-Za-z]{2}$/;

      return {
        ...this.select2OptionsMultiple,
        closeOnSelect: false,
        allowClear: true,
        multiple: true,
        tags: true,
        tokenSeparators: [',', '\n'],
        data: [],
        createTag: (params) => {
          let term = $.trim(params.term);

          if (!term) {
            return null;
          }

          if (scope === 'country') {
            if (!country.test(term)) {
              return null;
            }
            term = term.toUpperCase();
          } else if (scope === 'ip') {
            if (!isValidIp(term)) {
              return null;
            }
          }

          return { id: term, text: term, newOption: true };
        },
        templateResult: (selection) => {
          const text = selection?.newOption
            ? `<span class="sn-custom-tag-text">Add</span> ${selection.text}`
            : selection.text;
          const classes = selection?.newOption ? 'new-option' : 'option';

          return $.parseHTML(`<div class="sn-custom-template-result ${classes}">${text}</div>`);
        },
      };
    },
  },
  watch: {
    'form.frequency'() {
      const timeUnitOptions = this.timeUnitOptions;

      if (timeUnitOptions && !timeUnitOptions.map(({ id }) => id).includes(this.form.time_unit)) {
        this.form.time_unit = timeUnitOptions[0].id;
      }

      // Some metrics (e.g. geo-IP) aren't offered for every frequency. When the selected
      // metric drops out of the options (e.g. switching to hourly), the select clears
      // visually but its v-model isn't reset, so its dependent fields linger. Clear the
      // metric and reset those fields explicitly.
      if (this.form.metric_type && !this.metricOptions.some(({ id }) => id === this.form.metric_type)) {
        this.form.metric_type = '';
        this.onMetricChange();
      }
    },
    'form.metric_type'(current, previous) {
      const changedPrivacyRequirement = this.metricHasMemberPrivacy && this.metricsWithPrivacy.indexOf(previous) === -1;

      // If we change from a metric with member privacy to a metric without member privacy
      // Force selec2 to update and deselect members with privacy
      if (previous && current !== previous && changedPrivacyRequirement) {
        this.triggerLazySelect('form-target-members');
      }
    },
    'form.scope'(current, previous) {
      // The geo-IP list select2 captures the scope in its createTag/label closures
      // at init time; remount it on scope change so the new validators + label apply,
      // and drop entries that were valid only under the previous scope.
      if (this.isGeoIpListMatchMetric && previous && current !== previous) {
        this.form.list = [];
        this.geoIpListKey += 1;
      }
    },
  },
  created() {
    extend('custom_target_rule', customTargetRule(this.authorIsTeamLead));

    if (this.createdWithPreset) {
      this.initFormWithPreset();
    } else {
      this.initForm();
    }
  },
  mounted() {
    this.initializeToolsSelect();
    this.initializeGeoIpList();
    // If Metric depends on Data Privacy, trigger lazy select to update and
    // potentially remove users whose privacy requirements changed
    if (this.isAppOrURLBasedMetric) {
      this.triggerLazySelect('form-target-members');
    }
  },
  methods: {
    ...mapActions(['getSlackStatus', 'setSlackStatus']),
    initializeGeoIpList() {
      // The geo-IP list is a tag select with no predefined options, so a saved list
      // loaded on edit has no <option> elements for select2's .val() to select. Seed
      // them here (the dialog mounts before AppSelect's deferred select2 init) so the
      // saved entries render as selected tags.
      if (!this.isGeoIpListMatchMetric || !this.form.list?.length) {
        return;
      }

      const select = $('#geo-ip-list-select');

      this.form.list.forEach((entry) => select.append(new Option(entry, entry, true, true)));
    },
    async triggerLazySelect(ref) {
      // Forcing lazySelect to update without clicking it
      await this.$refs[ref].fetchItems();
      this.$refs[ref].updateModelInput();
    },
    initializeToolsSelect() {
      const that = this;
      const toolSelect = $('#tools-select');

      this.initTools();
      toolSelect.select2(this.toolsSelect2Options);

      if (this.selectedTools) {
        this.tools = this.selectedTools.map((tool) => (isNaN(tool.id) ? tool.id : Number(tool.id)));

        // Due to select2's ajax and the existance of custom urls (tags) in our notifications, we need to programatically
        // create selected tools when editing a notification or when creating a notification with a template
        this.selectedTools.forEach((tool) => {
          if (tool.id) {
            $('#tools-select').append(new Option(tool.text, tool.id, true, true)).trigger('change');
          } else {
            $('#tools-select').append(new Option(tool, tool, true, true)).trigger('change');
          }
        });

        $('#tools-select').trigger({
          type: 'select2:select',
          params: {
            data: this.selectedTools,
          },
        });
      }

      toolSelect.on('select2:select', function (e) {
        const item = {
          id: e.params.data.id,
          name: e.params.data.text,
        };

        that.selectedTools.push(item);

        // Clear input text any time an option is selected/created
        $(this).parent().find('.select2-search__field').val('');
      });

      toolSelect.on('select2:unselect', (e) => {
        const toolId = e.params.data.id;

        this.selectedTools = this.selectedTools.filter((item) => item.id !== toolId);
        // Removing custom url option from component if it's unselected,
        // otherwise it cannot be cleared and created again
        if (`${toolId}`.includes('custom-')) {
          $('#tools-select').find(`option[value='${e.params.data.id}']`).remove().trigger('change');
        }
      });
    },
    isTimeBasedMetric() {
      const timeBasedMetrics = SETTINGS_SMART_NOTIFICATIONS.options.timeBasedMetrics;

      return timeBasedMetrics.indexOf(this.form.metric_type) !== -1;
    },
    isCountMetric() {
      const countMetrics = SETTINGS_SMART_NOTIFICATIONS.options.countMetrics;

      return countMetrics.indexOf(this.form.metric_type) !== -1;
    },
    countMetricAddon() {
      switch (this.form.metric_type) {
        case 'users_per_ip':
          return 'users';
        case 'geo_ip_list_match':
          return 'matches';
        default:
          return 'changes';
      }
    },
    featureEnabled(featureKey) {
      const key = `${featureKey}_enabled`;

      return !!this.helpers?.[key];
    },
    initTools() {
      const data = this.form?.additional_data?.tools || this.form?.tools;
      const ids = data?.ids
        ? Object.entries(data.ids).map((tool) => ({
            id: tool[0],
            text: tool[1],
          }))
        : [];
      const names =
        data?.names?.map((toolName, index) => ({
          id: `custom-${index}`,
          text: toolName,
        })) || [];

      this.selectedTools = data ? [...ids, ...names] : [];
    },
    initForm() {
      let target = [];

      if (this.notification?.target) {
        this.targetAllMembers = this.notification.target.members.includes('all');

        target = this.targetAllMembers
          ? this.targetSelect
          : Object.fromEntries(
              Object.entries(this.notification.target).map(([key, value]) => [key, value.map((id) => Number(id))]),
            );
      }

      const recipients = {
        roles: this.notification?.recipients?.roles || [],
        members: this.notification?.recipients?.members?.map(Number) || [],
      };

      if (this.notification && this.type === 'edit') {
        this.form = {
          ...this.form,
          ...this.notification,
          metric_type: this.initMetricTypeAndTools(this.notification),
          // Seconds to HH:mm
          condition_value:
            SETTINGS_SMART_NOTIFICATIONS.options.timeBasedMetrics.includes(this.notification.metric_type) ||
            this.notification.metric_type === 'time_spent_in_tool'
              ? secondsToHours(this.notification.condition_value)
              : String(this.notification.condition_value),
          target,
          recipients,
          deliver_by: this.prepareDeliverBy(this.notification.deliver_by, 'edit'),
          time_unit: this.notification.time_unit,
          scope: this.notification.additional_data?.scope || 'ip',
          match_mode: this.notification.additional_data?.match_mode || 'in',
          list: this.notification.additional_data?.list || [],
        };

        if (target?.members.includes('all')) {
          this.targetAllMembers = true;
        } else {
          this.targetSelect = { ...this.targetSelect, ...target };
        }

        this.targetSelect = { ...this.targetSelect, ...target };
        this.recipientsSelect = { ...this.recipientsSelect, ...recipients };

        this.checkSlackStatus();
      } else {
        this.form =
          // prevent form to initialize with the old values, it doesn't detect changes to nested `deliver_by`
          JSON.parse(JSON.stringify(SETTINGS_SMART_NOTIFICATIONS.defaultFormSetting));
      }
    },
    initFormWithPreset() {
      const preset = JSON.parse(JSON.stringify(SETTINGS_SMART_NOTIFICATIONS.presets[this.preset]));
      const { target_all_members, recipients = { roles: [], members: [] }, ...formFields } = preset;

      this.form = {
        ...formFields,
        recipients,
        target: target_all_members ? { members: ['all'] } : { members: [] },
      };
      this.targetAllMembers = target_all_members;
      this.recipientsSelect = { ...this.recipientsSelect, ...recipients };
    },
    initMetricTypeAndTools(form) {
      const metricTypeDict = {
        application: 'time_spent_in_app',
        url: 'time_spent_in_url',
      };

      if (form.metric_type !== 'time_spent_in_tool') {
        return form.metric_type;
      }

      // If tools are coming from a template
      if (form.tools) {
        this.tools = form.tools;

        return;
      }

      if (form.additional_data && form.additional_data.tools) {
        this.tools = form.additional_data.tools.ids;

        return metricTypeDict[form.additional_data.tools.type];
      }

      return '';
    },
    selectTab(tab, input) {
      this.$set(this, `${tab}Tab`, input);
    },
    onMetricChange() {
      this.form.condition_value = '';
      this.tools = [];
      // Use Vue.set: in create mode, `defaultFormSetting` has none of these keys, so a
      // direct assignment would add a non-reactive property and v-model writes from the
      // scope/match_mode dropdowns would silently update without re-rendering.
      Vue.set(this.form, 'scope', this.isGeoIpChangesMetric || this.isGeoIpListMatchMetric ? 'ip' : null);
      Vue.set(this.form, 'match_mode', this.isGeoIpListMatchMetric ? 'in' : null);
      Vue.set(this.form, 'list', this.isGeoIpListMatchMetric ? [] : null);
      this.geoIpListKey += 1;
      $('#tools-select').empty().select2(this.toolsSelect2Options).val(null).trigger('change.select2');
      // Force select2 to update
      this.toolsKey += 1;
    },
    generateURL(url) {
      return applyTemplate(url);
    },
    resetTarget() {
      Vue.set(this.targetSelect, 'members', []);
      Vue.set(this.targetSelect, 'teams', []);
      Vue.set(this.targetSelect, 'occupations', []);
      // Force AppLazySelect to clean selected information
      this.targetKey += 1;
    },
    onTargetChange(value, type = 'select') {
      if (type === 'toggle') {
        this.form.target = value ? { members: [ALL_MEMBERS] } : { members: [] };
        this.resetTarget();
        this.targetAllMembers = value;
      } else {
        this.targetAllMembers = false;
        const values = { ...this.targetSelect };

        Vue.set(this.form, 'target', values);
      }

      this.selectedTargetMembers = this.form.target;
    },
    prepareTimeMetric(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);

      // 90000 => "25:00"
      return [String(hours).padStart(2, '0'), String(minutes).padStart(2, '0')].join(':');
    },
    prepareConditionValue(value) {
      // HH:mm to seconds
      if (value.includes(':')) {
        const [hours, minutes] = value.split(':');

        return Number(hours) * 3600 + Number(minutes) * 60;
      }

      return value;
    },
    // { String } Type: 'submit' | 'edit'
    // Depending on the type, we transform Object to Array or the other way round
    prepareDeliverBy(deliverBy, type) {
      if (type === 'submit') {
        const result = Object.keys(deliverBy).filter((key) => deliverBy[key]);

        return result.length ? result : ['email'];
      }

      if (type === 'edit') {
        const result = { slack: false, insights: true };

        deliverBy.forEach((key) => {
          result[key] = true;
        });

        return result;
      }
    },
    recalculateMetrics(newTimeUnit) {
      // hack to force metrics to update
      this.dummyTimeUnit = newTimeUnit;
    },
    async checkSlackStatus() {
      if (this.form.deliver_by.slack) {
        const recipients = Object.values(this.form.recipients).flat();

        if (recipients.length) {
          this.slackStatusLoading = true;
          await this.getSlackStatus(this.recipientsSelect);
          this.slackStatusLoading = false;
        } else {
          this.setSlackStatus({});
        }
      }
    },
    // If any change => reset Slack option, so we can match users properly
    onRecipientChange() {
      this.$set(this.form, 'recipients', this.recipientsSelect);
      this.checkSlackStatus();
    },
    emitClose() {
      this.$emit('close');
    },
    async validate() {
      const isValid = await this.$refs.observer.validate();

      if (isValid && !this.viewMode) {
        this.onSubmit();
      }
    },
    setErrors(errors) {
      this.$refs.observer.setErrors(errors);
    },
    preparedFormData() {
      const target =
        this.is_team_lead || this.authorIsTeamLead
          ? { members: this.form.target.members, teams: this.form.target.teams }
          : this.targetAllMembers
          ? { members: ['all'] }
          : this.form.target;
      const formData = {
        ...this.form,
        ...(this.isTimeBasedMetric() ? {} : { time_unit: null }), // do not pass time_unit for percentage
        condition_value: this.prepareConditionValue(this.form.condition_value),
        target,
        recipients: this.form.recipients,
        deliver_by: this.prepareDeliverBy(this.form.deliver_by, 'submit'),
      };

      if (this.isAppOrURLBasedMetric) {
        formData.tool_type = this.form.metric_type === 'time_spent_in_app' ? 'application' : 'url';
        formData.metric_type = 'time_spent_in_tool';
        formData.tool_ids = this.tools.filter((tool) => !isNaN(tool)).map(Number);
        formData.tool_names = this.tools
          .filter((tool) => isNaN(tool))
          .map((id) => this.selectedTools.find((tool) => tool.id === id)?.text || id);
      }

      return formData;
    },
    onSubmit() {
      this.$emit('submit', this.preparedFormData());
    },
    async fetcher(payload, memberType, attributes = null) {
      const attrs = {};

      if (memberType === 'app' || memberType === 'url') {
        attrs.show_unselected_ids = false;
      }

      if (attributes && memberType === 'user') {
        if (attributes.includes('privacy')) {
          attrs.privacy_enabled = this.metricHasMemberPrivacy;
        }
        if (attributes.includes('roles')) {
          attrs.roles = this.rolesOptions.map((role) => role.id);
        }
        if (attributes.includes('not_strict')) {
          attrs.strict = false;
        }
      }

      return await SmartNotificationsSettingsResource.members(
        { organizationId: this.organization_id },
        { ...payload, member_type: memberType, ...attrs },
        {
          abortReload: true,
        },
      ).then((response) => ({
        ...response,
        items: response.items.map((item) => ({
          ...item,
          disabled: item.privacy_enabled ? 'Privacy enabled' : false,
        })),
      }));
    },
  },
};
</script>
